import type { UploadDoc } from "@gsignatures/core";
import { useRef, useState } from "react";
import { ActionBar } from "../components/ActionBar";
import { Icon } from "../components/Icon";
import { useFocusHeading } from "../lib/hooks";
import { PictureError, preparePicture } from "../lib/picture";
import type { FlowCtx } from "../state/flow";
import { S } from "../strings";

/**
 * Pictures the sender asked for — an ID proof (owner, 2026-10-25). The signer takes a photo or picks one;
 * the page shrinks it (lib/picture.ts) and sends it. One or two pictures (front and back). They can change
 * until the first signature; at completion they're sealed into a PDF with the signed documents.
 */
export function UploadScreen({ ctx, doc }: { ctx: FlowCtx; doc: UploadDoc }) {
  const { session, api, w } = ctx;
  const heading = useFocusHeading();
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const what = doc.title;
  const locked = Object.keys(session.progress.signed).length > 0;
  const full = doc.files.length >= doc.maxFiles;
  const canAdd = !locked && !full && busy === null;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setProblem(null);
    setBusy("add");
    try {
      const p = await preparePicture(file);
      const s = await ctx.run(() => api.addPicture(doc.id, p.dataUrl, p.name));
      if (s) ctx.update(s);
    } catch (err) {
      setProblem(S.upload.problem[err instanceof PictureError ? err.code : "unreadable"]);
    } finally {
      setBusy(null);
    }
  }

  async function remove(fileId: string) {
    if (busy !== null) return;
    setBusy(fileId);
    const s = await ctx.run(() => api.removePicture(doc.id, fileId));
    setBusy(null);
    if (s) ctx.update(s);
  }

  function next() {
    if (!doc.files.length) {
      setProblem(S.upload.needOne);
      return;
    }
    ctx.go("review");
  }

  return (
    <>
      <main className="page" id="main">
        <div className="panel panel-pad narrow-panel">
          <div className="stack">
            <div>
              <div className="eyebrow">{S.upload.eyebrow}</div>
              <h1 className="title" ref={heading} tabIndex={-1}>
                {S.upload.title(what)}
              </h1>
              <p className="lede">{doc.help || S.upload.tips}</p>
              <p className="muted small">{S.upload.lede(w)}</p>
            </div>

            {doc.files.length > 0 && (
              <ul className="pictures" aria-label={S.upload.count(doc.files.length, doc.maxFiles)}>
                {doc.files.map((f, i) => (
                  <li className="picture" key={f.id}>
                    <img src={api.pictureUrl(doc.id, f.id)} alt={S.upload.pictureAlt(i + 1, what)} loading="lazy" />
                    {!locked && (
                      <button type="button" className="btn-link" onClick={() => void remove(f.id)} disabled={busy !== null} aria-label={S.upload.removeLabel(i + 1)} aria-busy={busy === f.id}>
                        <Icon n="trash" size={14} /> {S.upload.remove}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {locked ? (
              <p className="help">{S.upload.locked}</p>
            ) : (
              !full && (
                <div className="row">
                  <button type="button" className="btn btn-quiet" onClick={() => camera.current?.click()} disabled={!canAdd} aria-busy={busy === "add"}>
                    <Icon n="camera" /> {busy === "add" ? S.upload.adding : S.upload.takePhoto}
                  </button>
                  <button type="button" className="btn btn-quiet" onClick={() => picker.current?.click()} disabled={!canAdd}>
                    <Icon n="image" /> {S.upload.choosePicture}
                  </button>
                  {/* capture: a phone opens the back camera; a computer opens the file picker. */}
                  <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void onFile(e)} data-testid="take-photo" />
                  <input ref={picker} type="file" accept="image/*" hidden onChange={(e) => void onFile(e)} data-testid="choose-picture" />
                </div>
              )
            )}
            <p className="muted small" aria-live="polite">
              {S.upload.count(doc.files.length, doc.maxFiles)}
              {doc.files.length === 1 && doc.maxFiles > 1 && !locked ? ` · ${S.upload.secondHint}` : ""}
            </p>
            {problem && (
              <div className="note flag" role="alert">
                <Icon n="alert" /> <span>{problem}</span>
              </div>
            )}
            <div className="row between desk-actions">
              <button type="button" className="btn btn-quiet" onClick={() => ctx.go("tax")}>
                <Icon n="back" /> {S.common.back}
              </button>
              <button type="button" className="btn btn-primary" onClick={next} disabled={busy !== null}>
                {S.common.continue} <Icon n="chev" />
              </button>
            </div>
          </div>
        </div>
      </main>
      <ActionBar>
        <button type="button" className="btn btn-quiet" onClick={() => ctx.go("tax")}>
          {S.common.back}
        </button>
        <button type="button" className="btn btn-primary" onClick={next} disabled={busy !== null}>
          {S.common.continue}
        </button>
      </ActionBar>
    </>
  );
}
